import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";

import { CHECKER_KEY, useCheckerAccount, useCheckerKinds } from "@/api/checkers";
import { api, errorMessage, type Schemas, unwrap } from "@/api/client";
import {
  PROVIDER_ACCOUNTS_KEY,
  type ProviderAccount,
  usd,
  useProviderAccounts,
  useProviderBalance,
  useProviderKinds,
} from "@/api/providers";
import { ME_KEY } from "@/auth";
import ConfirmDialog from "@/components/confirm-dialog";
import Icon from "@/components/icon";
import IconBadge from "@/components/icon-badge";
import {
  List,
  ListHeader,
  ListItem,
  ListItemContent,
  ListItemIcon,
  ListItemTrailing,
  ListTitle,
} from "@/components/list";
import Loader from "@/components/loader";
import PageHeader from "@/components/page-header";
import Text from "@/components/text";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SETTINGS_KEY = ["settings"] as const;

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function SettingsPage() {
  const settings = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => unwrap(api.GET("/api/settings")),
  });

  return (
    <div className="flex w-full max-w-2xl flex-col gap-5">
      <PageHeader title="Settings" />
      {settings.isPending && (
        <div className="grid place-items-center py-16 text-muted-foreground">
          <Loader />
        </div>
      )}
      {settings.isError && <Text.Danger>{errorMessage(settings.error)}</Text.Danger>}
      {settings.data && <ApiKeySection settings={settings.data} />}
      <ProvidersSection />
      <CheckerSection />
      {settings.data && <ServerSection settings={settings.data} />}
      <PasswordSection />
    </div>
  );
}

function useSaveSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas["SettingsUpdate"]) => unwrap(api.PUT("/api/settings", { body })),
    onSuccess: (data) => {
      queryClient.setQueryData(SETTINGS_KEY, data);
      void queryClient.invalidateQueries({ queryKey: ME_KEY });
      void queryClient.invalidateQueries({ queryKey: ["auto-email-offer"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
}

function ApiKeySection({ settings }: { settings: Schemas["SettingsOut"] }) {
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState("");
  const save = useSaveSettings();

  const removeKey = useMutation({
    mutationFn: () => unwrap(api.DELETE("/api/settings/api-key")),
    onSuccess: (data) => {
      queryClient.setQueryData(SETTINGS_KEY, data);
      void queryClient.invalidateQueries({ queryKey: ME_KEY });
      toast("API key removed");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const test = useMutation({
    mutationFn: () => unwrap(api.POST("/api/settings/test-key")),
    onSuccess: (r) => {
      if (!r.ok) toast.error(`Key refused: ${r.message ?? r.error_code}`);
      else toast.success("Key works");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    save.mutate(
      { api_key: apiKey.trim() },
      {
        onSuccess: () => {
          setApiKey("");
          toast.success("API key saved");
          test.reset();
        },
      },
    );
  };

  const key = settings.api_key;
  const offer = test.data?.ok ? test.data.auto_email : null;

  return (
    <List>
      <ListHeader>
        <ListTitle>Seller API key</ListTitle>
      </ListHeader>

      <ListItem>
        <ListItemIcon>
          <IconBadge tone={key ? "green" : "gray"}>
            <Icon name="key" variant="Bold" size={18} />
          </IconBadge>
        </ListItemIcon>
        <ListItemContent>
          {key ? (
            <>
              <Text className="tabular-nums">Ends in {key.last4}</Text>
              <Text.Hint size="caption">Saved {formatWhen(key.set_at)}, stored encrypted</Text.Hint>
            </>
          ) : (
            <>
              <Text>No key saved</Text>
              <Text.Hint size="caption">Runs can't start without one</Text.Hint>
            </>
          )}
        </ListItemContent>
        {key && (
          <ListItemTrailing>
            <Button variant="outline" size="sm" loading={test.isPending} onClick={() => test.mutate()}>
              Test
            </Button>
            <ConfirmDialog
              trigger={
                <Button variant="destructive" size="sm">
                  Remove
                </Button>
              }
              title="Remove the API key?"
              description="Runs already going keep working until they finish. New runs need a key."
              confirmLabel="Remove key"
              destructive
              onConfirm={() => removeKey.mutate()}
            />
          </ListItemTrailing>
        )}
      </ListItem>

      {test.data?.ok && (
        <ListItem>
          <ListItemContent>
            <Text.Hint size="caption">
              {offer?.available
                ? `Auto email available at ${offer.price ?? 0} per account, up to ${offer.timeout_seconds ?? "?"} s`
                : "Auto email isn't offered for this key"}
            </Text.Hint>
          </ListItemContent>
        </ListItem>
      )}

      <form onSubmit={submit} className="flex flex-col gap-2 px-(--list-inset) pt-2 pb-4">
        <Label htmlFor="api-key" className="font-normal">
          {key ? "Replace with a new key" : "Paste your key"}
        </Label>
        <div className="flex gap-2">
          <Input
            id="api-key"
            type="password"
            value={apiKey}
            placeholder="Paste the key an admin created for you"
            onChange={(e) => setApiKey(e.target.value)}
          />
          <Button type="submit" disabled={!apiKey.trim()} loading={save.isPending}>
            Save
          </Button>
        </div>
        <Text.Hint size="caption" className="ps-1">
          The key is shown only once when it's created. Only its last 4 characters are kept visible here.
        </Text.Hint>
      </form>
    </List>
  );
}

function ProvidersSection() {
  const queryClient = useQueryClient();
  const kinds = useProviderKinds();
  const accounts = useProviderAccounts();
  const [provider, setProvider] = useState("vinu");
  const [label, setLabel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const kind = kinds.data?.find((k) => k.name === provider);

  const add = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/provider-accounts", {
          body: { provider, label: label.trim() || null, api_key: apiKey.trim() },
        }),
      ),
    onSuccess: () => {
      setApiKey("");
      setLabel("");
      void queryClient.invalidateQueries({ queryKey: PROVIDER_ACCOUNTS_KEY });
      toast.success("Provider account added");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE("/api/provider-accounts/{account_id}", { params: { path: { account_id: id } } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROVIDER_ACCOUNTS_KEY });
      toast("Provider account removed");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <List>
      <ListHeader>
        <ListTitle>Number providers</ListTitle>
      </ListHeader>

      {accounts.data?.length === 0 && (
        <Text.Hint className="px-(--list-inset) py-2">
          Add a provider key to buy numbers for a run instead of pasting a list.
        </Text.Hint>
      )}
      {accounts.data?.map((a) => (
        <ProviderAccountRow key={a.id} account={a} onRemove={() => remove.mutate(a.id)} />
      ))}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (apiKey.trim()) add.mutate();
        }}
        className="flex flex-col gap-2 px-(--list-inset) pt-2 pb-4"
      >
        <div className="grid gap-2 sm:grid-cols-[10rem_1fr]">
          {(kinds.data?.length ?? 0) > 1 ? (
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger aria-label="Provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {kinds.data?.map((k) => (
                  <SelectItem key={k.name} value={k.name}>
                    {k.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input value={kind?.title ?? "Vinu"} disabled aria-label="Provider" />
          )}
          <Input
            aria-label="Name (optional)"
            placeholder="Name (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Input
            type="password"
            aria-label="Provider API key"
            placeholder="Paste the provider's API key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <Button type="submit" disabled={!apiKey.trim()} loading={add.isPending}>
            Add
          </Button>
        </div>
        <Text.Hint size="caption" className="ps-1">
          {kind?.key_hint ?? "Your key at the provider."} The key is checked before it's saved, and stored encrypted.
        </Text.Hint>
      </form>
    </List>
  );
}

function CheckerSection() {
  const queryClient = useQueryClient();
  const kinds = useCheckerKinds();
  const account = useCheckerAccount();
  const kind = kinds.data?.[0];
  const [values, setValues] = useState<Record<string, string>>({});
  const filled = !!kind && kind.fields.every((f) => values[f.name]?.trim());

  const save = useMutation({
    mutationFn: () => {
      if (!kind) throw new Error("No checker to save.");
      return unwrap(api.PUT("/api/checker-account", { body: { checker: kind.name, credentials: values } }));
    },
    onSuccess: (data) => {
      queryClient.setQueryData(CHECKER_KEY, data);
      setValues({});
      toast.success("Number checker saved");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: () => unwrap(api.DELETE("/api/checker-account")),
    onSuccess: () => {
      queryClient.setQueryData(CHECKER_KEY, null);
      toast("Number checker removed");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const saved = account.data;
  return (
    <List>
      <ListHeader>
        <ListTitle>Number checker</ListTitle>
      </ListHeader>

      {saved ? (
        <ListItem>
          <ListItemIcon>
            <IconBadge tone="green">
              <Icon name="search" variant="Bold" size={18} />
            </IconBadge>
          </ListItemIcon>
          <ListItemContent>
            <Text>{saved.title}</Text>
            <Text.Hint size="caption" className="tabular-nums">
              Key ends in {saved.key_last4} · saved {formatWhen(saved.created_at)}
            </Text.Hint>
          </ListItemContent>
          <ListItemTrailing>
            <ConfirmDialog
              trigger={
                <Button variant="destructive" size="sm">
                  Remove
                </Button>
              }
              title="Remove the number checker?"
              description="Bought numbers are then used without checking them first."
              confirmLabel="Remove"
              destructive
              onConfirm={() => remove.mutate()}
            />
          </ListItemTrailing>
        </ListItem>
      ) : (
        <Text.Hint className="px-(--list-inset) py-2">
          With a checker, every bought number is checked for an existing Telegram account before any code is requested.
          Taken numbers are given back for a refund instead of being tried and lost.
        </Text.Hint>
      )}

      {kind && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (filled) save.mutate();
          }}
          className="flex flex-col gap-2 px-(--list-inset) pt-2 pb-4"
        >
          <Label className="font-normal">{saved ? "Replace with new credentials" : kind.title}</Label>
          <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            {kind.fields.map((f) => (
              <Input
                key={f.name}
                type={f.secret ? "password" : "text"}
                aria-label={f.label}
                placeholder={f.label}
                value={values[f.name] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              />
            ))}
            <Button type="submit" disabled={!filled} loading={save.isPending}>
              Save
            </Button>
          </div>
          <Text.Hint size="caption" className="ps-1">
            The credentials are tried on one number before they're saved (it uses one check), and stored encrypted.
          </Text.Hint>
        </form>
      )}
    </List>
  );
}

function ProviderAccountRow({ account, onRemove }: { account: ProviderAccount; onRemove: () => void }) {
  const balance = useProviderBalance(account.id);
  return (
    <ListItem>
      <ListItemIcon>
        <IconBadge tone="blue">
          <Icon name="sim" variant="Bold" size={18} />
        </IconBadge>
      </ListItemIcon>
      <ListItemContent>
        <Text>{account.label}</Text>
        <Text.Hint size="caption" className="tabular-nums">
          {account.provider_title} · key ends in {account.key_last4}
        </Text.Hint>
      </ListItemContent>
      <ListItemTrailing className="gap-3">
        {balance.isError ? (
          <Text.Danger size="caption">{errorMessage(balance.error)}</Text.Danger>
        ) : (
          <Text className="tabular-nums" weight="medium">
            {balance.isPending ? "…" : usd(balance.data?.balance)}
          </Text>
        )}
        <ConfirmDialog
          trigger={
            <Button variant="destructive" size="sm">
              Remove
            </Button>
          }
          title="Remove this provider account?"
          description="Runs can no longer buy numbers with it. Money on the account stays with the provider."
          confirmLabel="Remove"
          destructive
          onConfirm={onRemove}
        />
      </ListItemTrailing>
    </ListItem>
  );
}

function ServerSection({ settings }: { settings: Schemas["SettingsOut"] }) {
  const [baseUrl, setBaseUrl] = useState(settings.tarapi_base_url ?? "");
  const save = useSaveSettings();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      { tarapi_base_url: baseUrl.trim() || null },
      {
        onSuccess: (data) => {
          setBaseUrl(data.tarapi_base_url ?? "");
          toast.success("Saved");
        },
      },
    );
  };

  return (
    <List>
      <ListHeader>
        <ListTitle>Server</ListTitle>
      </ListHeader>
      <form onSubmit={submit} className="flex flex-col gap-2 px-(--list-inset) pt-1 pb-4">
        <Label htmlFor="base-url" className="font-normal">
          Base URL
        </Label>
        <div className="flex gap-2">
          <Input
            id="base-url"
            type="url"
            value={baseUrl}
            placeholder={settings.default_tarapi_base_url}
            onChange={(e) => setBaseUrl(e.target.value)}
          />
          <Button type="submit" variant="outline" loading={save.isPending}>
            Save
          </Button>
        </div>
        <Text.Hint size="caption" className="ps-1">
          Your runs use {settings.effective_tarapi_base_url}. Leave empty for the default.
        </Text.Hint>
      </form>
    </List>
  );
}

function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const change = useMutation({
    mutationFn: () =>
      unwrap(api.PUT("/api/auth/password", { body: { current_password: current, new_password: next } })),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      toast.success("Password changed. Other browsers were signed out.");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <List>
      <ListHeader>
        <ListTitle>Your password</ListTitle>
      </ListHeader>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          change.mutate();
        }}
        className="grid gap-3 px-(--list-inset) pt-1 pb-4 sm:grid-cols-2"
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="pw-current" className="font-normal">
            Current password
          </Label>
          <Input
            id="pw-current"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="pw-new" className="font-normal">
            New password
          </Label>
          <Input
            id="pw-new"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
          />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" variant="outline" loading={change.isPending}>
            Change password
          </Button>
        </div>
      </form>
    </List>
  );
}
